import { useQuery } from "@tanstack/react-query";
import type { PlanGroup, PlanItem } from "../../entities/ledger/types";
import { apiRequest } from "../../shared/api/client";
import { listPlanGroups, planningMonth, type PlanningPeriod } from "./api";

export interface PlanningTargets {
  items: PlanItem[];
  groups: PlanGroup[];
}

function listPlanItems(bookID: string, period: PlanningPeriod): Promise<{ planItems: PlanItem[] }> {
  const query = new URLSearchParams({ from: period.start, to: period.end });
  return apiRequest(`/v1/books/${encodeURIComponent(bookID)}/planning/plan-items?${query}`);
}

// The two things a transaction can be assigned to, loaded together so the
// picker can offer both without the caller knowing there are two endpoints.
// Plan items belong to a period; groups do not, so every active group is
// offered whatever month is being read.
export function usePlanningTargets(bookID: string, period: PlanningPeriod = planningMonth(0)) {
  return useQuery<PlanningTargets>({
    queryKey: ["planning-targets", bookID, period.key],
    enabled: bookID !== "",
    queryFn: async () => {
      const [items, groups] = await Promise.all([listPlanItems(bookID, period), listPlanGroups(bookID)]);
      return {
        items: items.planItems.filter((item) => !item.cancelledAt),
        groups: groups.groups.filter((view) => !view.group.archivedAt).map((view) => view.group),
      };
    },
  });
}
